import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  CheckCircle,
  User,
  Phone,
  Mail,
  CreditCard,
  Image,
  ZoomIn,
  X
} from 'lucide-react';
import Button from '../../components/ui/Button';
import { useApp } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import './CNICEntry.css';

function CNICEntry() {
  const navigate = useNavigate();
  const { brokerUsers, setCurrentTransaction, resetUserSession } = useApp();
  const { brokerConfig, brokerName, brokerLogo, getBrokerPath } = useBroker();
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);

  useEffect(() => {
    resetUserSession();
  }, [resetUserSession]);

  // Filter users by current broker
  const filteredUsers = useMemo(() => {
    return brokerUsers.filter(user =>
      user.broker?.toLowerCase() === brokerName?.toLowerCase() ||
      user.broker?.toLowerCase().includes(brokerConfig.id)
    );
  }, [brokerUsers, brokerName, brokerConfig.id]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleContinue = () => {
    if (!selectedUser) return;

    setIsLoading(true);

    setTimeout(() => {
      setCurrentTransaction({
        userId: selectedUser.id,
        userName: selectedUser.name,
        userCnic: selectedUser.cnic,
        userPhone: selectedUser.phone,
        userEmail: selectedUser.email,
        broker: selectedUser.broker,
        brokerPortal: brokerConfig.id, // Track which portal was used
      });

      navigate(getBrokerPath('/account-dashboard'));
      setIsLoading(false);
    }, 800);
  };

  return (
    <div className="cnic-container animate-fade-in">
      {/* Unified Main Card */}
      <div className="cnic-main-card">
        {/* Header Section */}
        <div className="cnic-header" style={{ background: brokerConfig.theme.headerGradient }}>
          <div className="header-icon-wrapper">
            <div className="header-logo">
              <img src={brokerLogo} alt={brokerName} className="broker-logo" />
            </div>
          </div>

          <h1 className="header-title">{brokerName} API Data</h1>
          <p className="header-subtitle">Select a user to proceed</p>

          <div className="header-features">
            <div className="feature-pill">
              <Shield size={14} />
              <span>KYC Verified</span>
            </div>
            <div className="feature-pill">
              <CheckCircle size={14} />
              <span>API Connected</span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="cnic-content">
          {/* Users List from Broker API */}
          <div className="broker-users-section">
            <div className="section-label">
              <User size={16} />
              <span>Registered Users ({filteredUsers.length})</span>
            </div>

            {filteredUsers.length === 0 ? (
              <div className="no-users-message">
                <User size={48} style={{ color: '#94a3b8' }} />
                <p>No users found for {brokerName}</p>
                <span>Users registered with {brokerName} will appear here</span>
              </div>
            ) : (
              <div className="users-list">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`user-card ${selectedUser?.id === user.id ? 'selected' : ''}`}
                    onClick={() => handleUserSelect(user)}
                  >
                    {/* User Header */}
                    <div className="user-card-header">
                      <div className="user-avatar" style={{ background: `linear-gradient(135deg, ${brokerConfig.primaryColor} 0%, ${brokerConfig.secondaryColor} 100%)` }}>
                        {user.name.charAt(0)}
                      </div>
                      <div className="user-main-info">
                        <h3 className="user-name">{user.name}</h3>
                        <span className="user-broker">{user.broker}</span>
                      </div>
                      {selectedUser?.id === user.id && (
                        <CheckCircle size={20} className="selected-check" style={{ color: brokerConfig.primaryColor }} />
                      )}
                    </div>

                    {/* User Details - Horizontal Layout */}
                    <div className="user-details-inline">
                      <div className="detail-chip">
                        <CreditCard size={12} />
                        <span>{user.cnic}</span>
                      </div>
                      <div className="detail-chip">
                        <Phone size={12} />
                        <span>{user.phone}</span>
                      </div>
                      <div className="detail-chip">
                        <Mail size={12} />
                        <span>{user.email}</span>
                      </div>
                    </div>

                    {/* CNIC Images */}
                    <div className="cnic-images-section">
                      <div className="cnic-images-label">
                        <Image size={14} />
                        <span>CNIC Documents</span>
                      </div>
                      <div className="cnic-images-grid">
                        <div
                          className="cnic-image-box"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (user.cnicFrontImage) setZoomedImage({ src: user.cnicFrontImage, title: 'CNIC Front' });
                          }}
                        >
                          {user.cnicFrontImage ? (
                            <>
                              <img src={user.cnicFrontImage} alt="CNIC Front" />
                              <div className="cnic-zoom-overlay">
                                <ZoomIn size={16} />
                              </div>
                            </>
                          ) : (
                            <div className="cnic-placeholder">
                              <Image size={20} />
                              <span>Front</span>
                            </div>
                          )}
                          <span className="cnic-image-label">Front</span>
                        </div>
                        <div
                          className="cnic-image-box"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (user.cnicBackImage) setZoomedImage({ src: user.cnicBackImage, title: 'CNIC Back' });
                          }}
                        >
                          {user.cnicBackImage ? (
                            <>
                              <img src={user.cnicBackImage} alt="CNIC Back" />
                              <div className="cnic-zoom-overlay">
                                <ZoomIn size={16} />
                              </div>
                            </>
                          ) : (
                            <div className="cnic-placeholder">
                              <Image size={20} />
                              <span>Back</span>
                            </div>
                          )}
                          <span className="cnic-image-label">Back</span>
                        </div>
                      </div>
                    </div>

                    {/* KYC Status */}
                    <div className="kyc-status" style={{ background: brokerConfig.theme.badgeColor, color: brokerConfig.theme.badgeTextColor }}>
                      <CheckCircle size={14} />
                      <span>KYC Verified</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Continue Button */}
            <Button
              type="button"
              variant="primary"
              size="large"
              fullWidth
              loading={isLoading}
              icon={ArrowRight}
              iconPosition="right"
              disabled={!selectedUser}
              onClick={handleContinue}
              style={{
                background: `linear-gradient(135deg, ${brokerConfig.primaryColor} 0%, ${brokerConfig.secondaryColor} 100%)`,
                borderColor: brokerConfig.primaryColor
              }}
            >
              Continue with Selected User
            </Button>
          </div>
        </div>

        {/* Footer Section */}
        <div className="cnic-footer">
          <div className="process-flow">
            <div className="process-step active">
              <div className="process-dot" style={{ background: `linear-gradient(135deg, ${brokerConfig.primaryColor} 0%, ${brokerConfig.secondaryColor} 100%)` }}></div>
              <span style={{ color: brokerConfig.primaryColor }}>Select User</span>
            </div>
            <div className="process-line"></div>
            <div className="process-step">
              <div className="process-dot"></div>
              <span>Select Account</span>
            </div>
            <div className="process-line"></div>
            <div className="process-step">
              <div className="process-dot"></div>
              <span>Verify & Pay</span>
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="zoom-modal-overlay" onClick={() => setZoomedImage(null)}>
          <div className="zoom-modal" onClick={(e) => e.stopPropagation()}>
            <div className="zoom-modal-header">
              <h3>{zoomedImage.title}</h3>
              <button className="zoom-close-btn" onClick={() => setZoomedImage(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="zoom-modal-body">
              <img src={zoomedImage.src} alt={zoomedImage.title} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CNICEntry;
